import type { RefObject } from 'react';
import React, { forwardRef } from 'react';
import { ColumnConfig, SortConfig, FilterConfig } from '@/shared/types/table';
import { isColumnLockable, isColumnResizable } from '../../utils/columnLockable';
import { HeaderCell } from '../molecules';
import { SelectionCheckbox } from '../molecules/SelectionCheckbox';
import { cn } from '@/shared/utils/cn';

export interface TableHeaderProps<T = unknown> {
  columns: ColumnConfig<T>[];
  /** Anchos medidos en `TableContent` (ResizeObserver sobre `<thead>`). */
  columnWidthsRef?: RefObject<Map<string, number>>;
  sortConfig?: SortConfig;
  filters?: FilterConfig[];
  showSelection?: boolean;
  showExpand?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  resizable?: boolean;
  stickyHeader?: boolean;
  stickyCheckbox?: boolean;
  stickyActions?: boolean;
  onSelectAll?: (selected: boolean) => void;
  onSort?: (columnId: string) => void;
  onFilter?: (columnId: string) => void;
  onLock?: (columnId: string) => void;
  onResize?: (columnId: string, width: number, defaultWidthAtStart?: number) => void;
  onResizeDoubleClick?: (columnId: string) => void;
  onResizeHover?: (columnId: string, isHovering: boolean) => void;
  /** Clave que cambia cuando cambian las filas (p. ej. paginación); fuerza recalcular anchos auto. */
  rowsDataKey?: string;
  hasColumnMaxWidthAuto?: boolean;
  /** Por defecto de bloqueo a nivel tabla; las columnas pueden anular con `lockable`. */
  columnsLockable?: boolean;
}

function columnWidthFromRef(
  columnWidthsRef: RefObject<Map<string, number>> | undefined,
  col: ColumnConfig<unknown>,
  fallback: number
): number {
  const fromMap = columnWidthsRef?.current?.get(String(col.id));
  if (fromMap !== undefined && fromMap !== null && Number.isFinite(fromMap)) return fromMap;
  if (typeof col.width === 'number') return col.width;
  if (typeof col.width === 'string') {
    const parsed = parseInt(col.width, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
}

const TableHeaderInner = forwardRef<HTMLTableSectionElement, TableHeaderProps<unknown>>(
  function TableHeader(
    {
      columns,
      columnWidthsRef,
      sortConfig,
      filters = [],
      showSelection = false,
      showExpand = false,
      allSelected = false,
      someSelected = false,
      resizable = true,
      stickyHeader = true,
      stickyCheckbox = true,
      stickyActions = true,
      onSelectAll,
      onSort,
      onFilter,
      onLock,
      onResize,
      onResizeDoubleClick,
      onResizeHover,
      rowsDataKey,
      hasColumnMaxWidthAuto = false,
      columnsLockable,
    },
    ref
  ) {
    const visibleColumns = columns.filter((col) => col.visible !== false);

    const sortedColumns = [
      ...visibleColumns.filter((col) => col.locked),
      ...visibleColumns.filter((col) => !col.locked),
    ];

    const hasLockedColumns = columns.some((col) => col.locked);

    return (
      <thead ref={ref} className="ui-bg-fill">
        <tr>
          {showSelection && (
            <th
              className={cn(
                'ui-border-b ui-border-default ui-bg-surface ui-px-4 ui-py-3',
                stickyCheckbox || hasLockedColumns
                  ? 'ui-sticky ui-isolate ui-z-[51]'
                  : stickyHeader
                    ? 'ui-sticky ui-z-[30]'
                    : '',
                stickyHeader && 'ui-top-0'
              )}
              data-column-id="selection"
              style={{
                width: 48,
                minWidth: 48,
                maxWidth: 48,
                ...((stickyCheckbox || hasLockedColumns) && {
                  left: 'var(--table-sticky-left-selection, 0px)',
                }),
              }}
            >
              <div className="ui-flex ui-justify-center">
                <SelectionCheckbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(checked) => onSelectAll?.(checked)}
                />
              </div>
            </th>
          )}

          {showExpand && (
            <th
              data-column-id="expand"
              className={cn(
                'ui-border-b ui-border-default ui-bg-surface',
                stickyCheckbox || hasLockedColumns
                  ? 'ui-sticky ui-isolate ui-z-[51]'
                  : stickyHeader
                    ? 'ui-sticky ui-z-[30]'
                    : '',
                stickyHeader && 'ui-top-0'
              )}
              style={{
                width: 48,
                minWidth: 48,
                maxWidth: 48,
                ...((stickyCheckbox || hasLockedColumns) && {
                  left: 'var(--table-sticky-left-expand, 48px)',
                }),
              }}
            />
          )}

          {sortedColumns.map((column, index) => {
            const isSorted = sortConfig?.columnId === column.id;
            const isFiltered = filters.some((f) => f.columnId === column.id);

            const lockedColumns = sortedColumns.filter((c) => c.locked);
            const isLastLocked =
              column.locked &&
              lockedColumns.length > 0 &&
              lockedColumns[lockedColumns.length - 1].id === column.id;

            const isActionsColumn = column.type === 'actions';

            // El posicionamiento ahora lo gestionan variables CSS inyectadas por el ResizeObserver
            // en TableContent. Pasamos 0 como fallback inicial.
            const stickyLeft = 0;
            const stickyRight = 0;

            return (
              <HeaderCell
                key={column.id}
                column={column as ColumnConfig<unknown>}
                sortDirection={isSorted ? sortConfig?.direction : null}
                filterActive={isFiltered}
                lockable={isColumnLockable(column, columnsLockable)}
                locked={column.locked}
                isLastLocked={isLastLocked}
                resizable={isColumnResizable(column, resizable)}
                stickyLeft={stickyLeft}
                stickyRight={isActionsColumn && stickyActions ? stickyRight : undefined}
                stickyHeader={stickyHeader}
                isActionsColumn={isActionsColumn && stickyActions}
                onSort={() => onSort?.(column.id)}
                onFilter={() => onFilter?.(column.id)}
                onLock={() => onLock?.(column.id)}
                onResize={(width, defaultWidthAtStart) =>
                  onResize?.(column.id, width, defaultWidthAtStart)
                }
                onResizeDoubleClick={() => onResizeDoubleClick?.(column.id)}
                onResizeHover={(isHovering) => onResizeHover?.(column.id, isHovering)}
                rowsDataKey={rowsDataKey}
                className={column.headerClassName}
                hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
              />
            );
          })}
        </tr>
      </thead>
    );
  }
);

TableHeaderInner.displayName = 'TableHeader';

export const TableHeader = TableHeaderInner as <T>(
  props: TableHeaderProps<T> & React.RefAttributes<HTMLTableSectionElement>
) => React.ReactElement | null;
